import { DateTime } from "luxon"
import { PaymentMethodType } from "../../../payments/paymentTypes"
import { BuyerParty } from "../../../payments/orderTypes"

export type Shop = Readonly<{
    id: number
    uid: string
    name: string
    caseNumber?: string
    shopNumber: string
    type: ShopTypes
    isActive: boolean
    demoMode: boolean
}>

export type ShopDetailsType = Readonly<{
    id: number
    uid: string
    merchant: Merchant
    supplier: Supplier
    name: string
    address: Address
    type: ShopTypes
    isActive: boolean
    demoMode: boolean
    caseNumber: string
    internalNote: string
    contactDetails: ContactDetails
    shopDetails: ShopDetails
    paymentDetails: PaymentDetails
    hardwarePackage: {
        id: number
    }[]
    deliveryDetails: DeliveryDetails
    brandingDetails: BrandingDetails
    entityDetails: EntityDetails
    deadline?: Deadline
    posDetails?: PosDetails
}>

export enum ShopTypes {
    MEETING_SERVICE = "MEETING_SERVICE",
    TAKE_AWAY = "TAKE_AWAY",
    LUNCH = "LUNCH",
    POS = "POS",
    SUBSCRIPTION = "SUBSCRIPTION",
}

type Merchant = Readonly<{
    id: number
    name: string
}>
type Supplier = Readonly<{
    id: number
    name: string
}>
type Address = Readonly<{
    plusCode: string
    street: string
    postalCode: string
    city: string
    country: string
}>
type ContactDetails = Readonly<{
    phoneNumber: string
    email: string
}>
type ShopDetails = Readonly<{
    isPublic: boolean
    publicShopDetails: PublicShopDetails
    settings: ShopSettings
}>

type PublicShopDetails = Readonly<{
    shopNumber: string
    shopUrl: string
    shopQR: string
    isVisibleForAppUsers: boolean
}>

type ShopSettings = Readonly<{
    showQRReceipt: boolean
    acceptMultipleCheckIns: boolean
}>
type PaymentDetails = Readonly<{
    // currencyCode: string
    // fillGuestDetails: boolean
    // paymentGateway: PaymentGateway
    paymentMethods: PaymentMethodDetails[]
}>
type PaymentGateway = Readonly<{
    id: number
    name: string
}>
type PaymentMethodDetails = Readonly<{
    value: string
    // translationKey: string
}>
type DeliveryDetails = Readonly<{
    description: string
}>
type BrandingDetails = Readonly<{
    description: string
    staffDetails: StaffDetail[]
    website: string
    images: string[]
    marketingTags: string[]
    smileyUrl: string
    openingHours: OpeningHour[]
    closingDays: ClosingDay[]
}>
type StaffDetail = Readonly<{
    position: string
    name: string
    description: string
    imageUrl: string
}>
type OpeningHour = Readonly<{
    day: string
    hasOpen: boolean
    from: string
    to: string
}>
type ClosingDay = Readonly<{
    Text: string
    date: string
}>
type EntityDetails = Readonly<{
    connectedCompanies: ConnectedCompany[]
    connectedDevices: ConnectedDevice[]
}>
export enum ConnectedCompanyStatus {
    PENDING = "PENDING",
    ACTIVE = "ACTIVE",
}
type ConnectedCompany = Readonly<{
    id: number
    name: string
    Address: string
    VATNumber: string
    status: ConnectedCompanyStatus
}>
export enum ConnectedDeviceType {
    ADYEN_POS = "ADYEN_POS",
    CLOUD_PRINTER = "CLOUD_PRINTER",
}
type ConnectedDevice = Readonly<{
    id: number
    name: string
    type: ConnectedDeviceType
}>

export type Deadline = Readonly<{
    purchases: {
        daysBefore: number
        timeOfDay: string
    }
    cancellation: {
        daysBefore: number
        timeOfDay: string
    }
}>

export type CreateOrEditShopFormValues = Readonly<{
    name: string
    type?: ShopTypes
    isActive: boolean
    demoMode: boolean
    caseNumber: string
    internalNote: string
    paymentDetails: PaymentDetails
    hardwarePackage?: ProductOrderLine[]
    shopDetails: {
        isPublic: boolean
        settings: ShopSettings
        publicShopDetails?: PublicShopDetails
    }
    deadline?: {
        purchases: {
            daysBefore: number
            timeOfDay: DateTime
        }
        cancellation: {
            daysBefore: number
            timeOfDay: DateTime
        }
    } | null
    posDetails?: PosDetails
}>

export type ShopItem = Readonly<{
    name: string
    type: ShopTypes
    isActive: boolean
    demoMode: boolean
    caseNumber: string
    internalNote: string
    paymentDetails: PaymentDetails
    shopDetails: {
        isPublic: boolean
        publicShopDetails?: PublicShopDetails
        settings: ShopSettings
    }
    deadline?: Deadline
    merchant?: {
        id: number
    }
    posDetails?: PosDetails
}>

// HardwarePackage
export type ShopHardware = Readonly<{
    shopType: ShopTypes
    productGroups: Group[]
}>
// Interfaces for options
export type SelectOption = Readonly<{
    label: string
    value: string
}>
export type ColorOption = Readonly<{
    color: string // Hex code for color
    value: string
}>

export enum ProductChoiceType {
    SELECT = "select",
    COLOR_SWATCH = "colorSwatch",
}

// Union type for different kinds of product choices
export type ProductChoice = Readonly<SelectProductChoice | ColorSwatchProductChoice>

type SelectProductChoice = Readonly<{
    id: string
    type: ProductChoiceType.SELECT
    selectOptions: SelectOption[] // Ensures selectOptions is available and mandatory
}>

type ColorSwatchProductChoice = Readonly<{
    id: string
    type: ProductChoiceType.COLOR_SWATCH
    colorOptions: ColorOption[] // Ensures colorOptions is available and mandatory
}>

export type CardDetails = Readonly<{
    imageUrl: string
}>

// Interface for a package in a group
export type Package = Readonly<{
    id: number
    name: string
    description: string
    productChoices: ProductChoice[]
    cardDetails: CardDetails
}>

// Interface for a group of packages
type Group = Readonly<{
    id: number
    name: string
    helpLink: string
    products: Package[]
}>

export type ProductOrderChoices = Readonly<{
    id: string
    value: string
}>

export type ProductOrderLine = Readonly<{
    productId: number
    items: number
    buyerParty: BuyerParty
    productChoices: ProductOrderChoices[]
}>

export type HardwareOrder = Readonly<{
    // payment: {
    //     method: PaymentMethodType
    // }
    deliveries: [
        {
            orderLines: ProductOrderLine[]
        },
    ]
}>

// PaymentMethod
export type PaymentMethod = Readonly<{
    value: PaymentMethodType
    isAllowedAsFixedMethod: boolean
    text: string
    imageUrl: string
}>

export enum LanguageCode {
    EN = "en",
    DA = "da",
}

export const ConfigureLanguage = {
    en: LanguageCode.EN,
    da: LanguageCode.DA,
}

export type PosDetails = Readonly<{
    defaultLanguage?: LanguageCode
    returnToHomeScreenDelay?: number
    returnToHomeAfterPurchase?: number
}>
